import { Auth } from "aws-amplify";

const getUser = async () => {
    try {
        const user = await Auth.currentAuthenticatedUser(); // Await the promise
        return user.username; // Return the username
    } catch (err) {
        console.log("Error in getUser: ", err);
        return null; // Handle the case where user is not authenticated
    }
};

export const convertToPdf = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/toolbox_convert_to_pdf`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const blob = await response.blob();
        const cd = response.headers.get("content-disposition");
        const match = cd.match(/filename="?([^"]+)"?/i);
        const filename = match?.[1] || "document.pdf";

        return { blob, filename };
    } catch (err) {
        throw new Error(`Error generating PDF: ${err.message}`);
    }
};

// ========================= Model Development =========================

export const getModelDevelopmentProjectDetails = async (projectId) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/model_development_projects_details/${encodeURIComponent(projectId)}`,
            { method: "GET" }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (err) {
        throw new Error(`Error fetching project details: ${err.message}`);
    }
};
export const updateModelDevelopmentProjectDetails = async (projectId, projectDetails) => {
    const username = await getUser();
    try {
        const res = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/update_model_development_project_details?project_id=${encodeURIComponent(projectId)}&username=${encodeURIComponent(username)}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(projectDetails),
            }
        );
        if (!res.ok) {
            const msg = await res.text().catch(() => "");
            throw new Error(`HTTP error! Status: ${res.status} ${msg}`);
        }
        return await res.json();
    } catch (err) {
        throw new Error(`Error updating project details: ${err.message}`);
    }
};

export const createModelDevelopmentProjectAPI = async (formData) => {
    const username = await getUser();
    formData.append("username", username);

    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/create_model_development_project`,
            {
                method: "POST",
                body: formData,
            }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in creating model development project: ${error.message}`);
    }
};

export const getAllModelDevelopmentProjects = async () => {
    const username = await getUser();
    const formData = new FormData();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/get_all_model_development_projects`,
            { method: "POST", body: formData }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (err) {
        throw new Error(`Error fetching project details: ${err.message}`);
    }
};

export const deleteModelDevelopmentProject = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/delete_model_development_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Deleting Model Development Project: ${error.message}`);
    }
};

export const updateModelDevelopmentProjectTags = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/tag_model_development_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Updating Model Development Project Tags: ${error.message}`);
    }
};

export const inviteUserToModelDevelopmentProject = async (formData) => {
    const username = await getUser();
    formData.append("inviter_username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/invite_user_to_model_development_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Inviting User to Model Development Project: ${error.message}`);
    }
};

export const cloneModelDevelopmentProject = async (formData) => {
    const username = await getUser();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/clone_model_development_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Cloning Model Development Project: ${error.message}`);
    }
};

export const regenerate = async ({ markdownContent, files, feedback }) => {
    const username = await getUser();
    const form = new FormData();
    form.append("markdown_content", markdownContent);
    if (feedback) form.append("feedback", feedback);
    if (files && files.length) {
        for (const f of files) form.append("files", f);
    }
    form.append("username", username);
    const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/regenerate`, {
        method: "POST",
        body: form,
    });
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

// ========================= Toolbox =========================

export const createNewToolboxMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/create_new_toolbox_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in creating new toolbox mapping: ${error.message}`);
    }
};

export const updateToolboxMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/update_toolbox_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in updating toolbox mapping: ${error.message}`);
    }
};

export const deleteToolboxMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/delete_toolbox_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in deleting toolbox mapping: ${error.message}`);
    }
};


export const getToolboxMappings = async () => {
    const formData = new FormData();
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/get_all_toolbox_mappings`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in getting toolbox mappings: ${error.message}`);
    }
};

export const createCodeMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/create_code_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in creating code mapping: ${error.message}`);
    }
};

export const updateCodeMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/update_code_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in updating code mapping: ${error.message}`);
    }
};

export const deleteCodeMapping = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/delete_code_mapping`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in deleting code mapping: ${error.message}`);
    }
};

export const getCodeMappings = async () => {
    const formData = new FormData();
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/get_all_code_mappings`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in getting code mappings: ${error.message}`);
    }
};


export const applyCodeToUserAPI = async (formData) => {
    const username = await getUser();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/apply_code_to_user`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error in applying code to user: ${error.message}`);
    }
};


export const uploadProjectFile = async (projectId, file) => {
    const form = new FormData();
    form.append("files", file);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/upload_file/${encodeURIComponent(projectId)}`,
        { method: "POST", body: form }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const deleteUploadedFile = async (projectId, fileId) => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("file_id", fileId);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/delete_uploaded_file`,
        { method: "POST", body: formData }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const setNextStep = async (projectId, step) => {
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/next_step?project_id=${encodeURIComponent(projectId)}&step=${encodeURIComponent(step)}`,
        { method: "POST" }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};


export const saveNewVersion = async (projectId, mdType, newMd) => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("md_type", mdType);
    formData.append("new_md", newMd);
    const url = `${process.env.REACT_APP_FASTAPI_URL}/save_new_version`;
    const res = await fetch(url, { method: "POST", body: formData });
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const generateOverview = async (projectId) => {
    const formData = new FormData();
    const username = await getUser();
    formData.append("project_id", projectId);
    formData.append("username", username);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/generate_overview`,
        {
            method: "POST",
            body: formData
        }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const generateModelDevelopmentPlan = async (projectId) => {
    const formData = new FormData();
    const username = await getUser();
    formData.append("project_id", projectId);
    formData.append("username", username);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/generate_model_development_plan`,
        {
            method: "POST",
            body: formData
        }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const publishProject = async (projectId) => {
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/publish?project_id=${encodeURIComponent(projectId)}`,
        { method: "POST" }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};


export const getToolboxesForUserAPI = async () => {
    const username = await getUser();
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/get_toolboxes_for_user/${username}`,
        { method: "GET" }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

// ========================= Model Validation =========================


export const getAllModelValidationProjectsAPI = async () => {
    const username = await getUser();
    const formData = new FormData();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/get_all_model_validation_projects`,
            { method: "POST", body: formData }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (err) {
        throw new Error(`Error fetching project details: ${err.message}`);
    }
};


export const createModelValidationProjectAPI = async (formData) => {
    const username = await getUser();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/create_model_validation_project`,
            {
                method: "POST",
                body: formData,
            }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Creating Model Validation Project: ${error.message}`);
    }
};

export const getModelValidationProjectDetails = async (projectId) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/model_validation_projects_details/${encodeURIComponent(projectId)}`,
            { method: "GET" }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (err) {
        throw new Error(`Error fetching project details: ${err.message}`);
    }
};

export const updateModelValidationProjectDetails = async (projectId, projectDetails) => {
    const username = await getUser();
    try {
        const res = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/update_model_validation_project_details?project_id=${encodeURIComponent(projectId)}&username=${encodeURIComponent(username)}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(projectDetails),
            }
        );

        if (!res.ok) {
            const msg = await res.text().catch(() => "");
            throw new Error(`HTTP error! Status: ${res.status} ${msg}`);
        }
        return await res.json();
    } catch (err) {
        throw new Error(`Error updating project details: ${err.message}`);
    }
};

export const uploadProjectFileValidation = async (projectId, file) => {
    const form = new FormData();
    form.append("files", file);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/upload_file_validation/${encodeURIComponent(projectId)}`,
        { method: "POST", body: form }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const deleteUploadedFileValidation = async (projectId, fileId) => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("file_id", fileId);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/delete_uploaded_file_validation`,
        { method: "POST", body: formData }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const setNextStepValidation = async (projectId, step) => {
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/next_step_validation?project_id=${encodeURIComponent(projectId)}&step=${encodeURIComponent(step)}`,
        { method: "POST" }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const saveNewVersionValidation = async (projectId, mdType, newMd) => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("md_type", mdType);
    formData.append("new_md", newMd);
    const url = `${process.env.REACT_APP_FASTAPI_URL}/save_new_version_validation`;
    const res = await fetch(url, { method: "POST", body: formData });
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const generateOverviewValidation = async (projectId) => {
    const formData = new FormData();
    const username = await getUser();
    formData.append("project_id", projectId);
    formData.append("username", username);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/generate_overview_validation`,
        {
            method: "POST",
            body: formData
        }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const generateModelValidationPlan = async (projectId) => {
    const formData = new FormData();
    const username = await getUser();
    formData.append("project_id", projectId);
    formData.append("username", username);
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/generate_model_validation_plan`,
        {
            method: "POST",
            body: formData
        }
    );
    if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};

export const publishProjectValidation = async (projectId) => {
    const res = await fetch(
        `${process.env.REACT_APP_FASTAPI_URL}/publish_validation?project_id=${encodeURIComponent(projectId)}`,
        { method: "POST" }
    );
    if (!res.ok) {
        const msg = (await res.text()).catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
    }
    return await res.json();
};



export const deleteModelValidationProjectAPI = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/delete_model_validation_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Deleting Model Validation Project: ${error.message}`);
    }
};

export const updateModelValidationProjectTagsAPI = async (formData) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/tag_model_validation_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Updating Model Validation Project Tags: ${error.message}`);
    }
};

export const inviteUserToModelValidationProjectAPI = async (formData) => {
    const username = await getUser();
    formData.append("inviter_username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/invite_user_to_model_validation_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Inviting User to Model Validation Project: ${error.message}`);
    }
};

export const cloneModelValidationProjectAPI = async (formData) => {
    const username = await getUser();
    formData.append("username", username);
    try {
        const response = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/clone_model_validation_project`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(`Error Cloning Model Validation Project: ${error.message}`);
    }
};